// Utils for return payload and summary grouping
// Pure, reusable helpers with no React dependencies

export const parseMaterialKey = (materialKey) => {
  const [hardwareId, subscriptionId] = (materialKey || "").split("|");
  return { hardwareId, subscriptionId };
};

export const getVid = (v) => v?.id || v?._stableId || v?.vehicle || v?.vin;

export const splitAndTrim = (val) =>
  (val || "")
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);

export const includesWorkticket = (val, wtId) => splitAndTrim(val).includes(wtId);

export const collectWorkticketCounts = (records) => {
  const counts = {};
  records.forEach((r) => {
    if (r?.workTicketId) {
      splitAndTrim(r.workTicketId).forEach((wt) => {
        counts[wt] = (counts[wt] || 0) + 1;
      });
    }
  });
  return counts;
};

export const normalizeToSet = (maybeSetOrArray) => (maybeSetOrArray instanceof Set ? maybeSetOrArray : new Set(maybeSetOrArray || []));

export const isDisabledMapping = (disabledSet, v, hw, sub, wt) => {
  const vid = getVid(v);
  const key = `${vid}|${hw}|${sub}|${wt}`;
  return disabledSet.has(key);
};

export const hasExactMapping = (disabledSet, v, hw, sub, wt) => {
  if (isDisabledMapping(disabledSet, v, hw, sub, wt)) return false;
  return (
    Array.isArray(v?.materialMapping) &&
    v.materialMapping.some((m) => m && m.hardwareId === hw && m.subscriptionId === sub && includesWorkticket(m.workticketId, wt))
  );
};

export const allocateForWorkticket = (vehiclesPool, hw, sub, wtId, needed, disabledSet) => {
  if (needed <= 0) return { chosen: [], remaining: vehiclesPool };
  const chosen = [];
  const remaining = [];
  for (let i = 0; i < vehiclesPool.length; i += 1) {
    const v = vehiclesPool[i];
    if (chosen.length < needed && hasExactMapping(disabledSet, v, hw, sub, wtId)) {
      chosen.push(v);
    } else {
      remaining.push(v);
    }
  }
  return { chosen, remaining };
};

export const collectDeliveryNumbers = (records) => {
  const set = new Set();
  records.forEach((r) => {
    splitAndTrim(r?.oldDeliveryNum).forEach((d) => set.add(d));
  });
  return Array.from(set).join(",");
};

export const deriveAddressContact = (allocatedVehs, subsetRecords) => {
  const addrSet = new Set(allocatedVehs.map((v) => v?.addressId).filter(Boolean));
  const contactSet = new Set(allocatedVehs.map((v) => v?.contactId).filter(Boolean));
  const derivedAddressId = addrSet.size ? Array.from(addrSet)[0] : subsetRecords.find((r) => r?.address?.addressId)?.address?.addressId || "";
  const derivedContactId = contactSet.size ? Array.from(contactSet)[0] : subsetRecords.find((r) => r?.contact?.contactId)?.contact?.contactId || "";
  return { derivedAddressId, derivedContactId };
};

export const buildVinMaterials = (v, hw, sub, wtId) => {
  if (Array.isArray(v?.materialMapping)) {
    const mmExact = v.materialMapping.find((m) => m && m.hardwareId === hw && m.subscriptionId === sub && includesWorkticket(m.workticketId, wtId));
    if (mmExact) return mmExact.vinMaterials || mmExact.id || "";
  }
  return "";
};

export const getNumericId = (val) => {
  const num = parseInt(val, 10);
  return Number.isNaN(num) ? null : String(num);
};

export const getWorkticketMaterialId = (key) => {
  if (key === null || key === undefined || key === "") return null;
  return String(key);
};

export const buildMaterialObject = (
  exemplar,
  hardwareId,
  subscriptionId,
  subsetRecords,
  allocatedVehs,
  returnProcessingCode,
  wtId,
  nextTempId,
  disabledEsnObjects,
  techRefId
) => {
  const materialLineId = null;
  let esnObjects = [];
  const seen = new Set();
  subsetRecords
    .filter((r) => r?.esn && r.hardwareId === hardwareId && r.subscriptionId === subscriptionId && includesWorkticket(r.workTicketId, wtId))
    .forEach((r) => {
      if (!seen.has(r.esn)) {
        seen.add(r.esn);
        const esnId = getNumericId(r.esnId) ?? getNumericId(r.id) ?? nextTempId();
        esnObjects.push({ esnId, esn: r.esn });
      }
    });

  // Filter out disabled ESNs if provided, matching on workticketId, hardwareId, and esn
  if (Array.isArray(disabledEsnObjects) && disabledEsnObjects.length > 0) {
    const toRemove = new Set(
      disabledEsnObjects
        .filter((d) => String(d.workticketId || "") === String(wtId) && String(d.hardwareId || "") === String(hardwareId))
        .map((d) => String(d.esn))
    );
    if (toRemove.size > 0) {
      esnObjects = esnObjects.filter((o) => !toRemove.has(String(o.esn)) && !toRemove.has(String(o.esnId)));
    }
  }

  return {
    id: getWorkticketMaterialId(exemplar.workticketMaterialIdKey),
    materialId: hardwareId,
    //techRefId: techRefId ?? exemplar?.techRefId ?? "",
    esnTobeReturned: esnObjects,
    summaryQty: allocatedVehs.length,
    materialLineId,
    returnProcessingCode,
    vins: allocatedVehs.map((v) => ({
      id: getVid(v),
      vinStatus: v?.vehicleStatus || "Ready",
      type: "VEHICLE",
      vinMaterials: buildVinMaterials(v, hardwareId, subscriptionId, wtId),
    })),
  };
};

export const mergeEntries = (entries) => {
  const mergedMap = {};
  entries.forEach((entry) => {
    const key = entry.workticketId;
    if (!mergedMap[key]) {
      mergedMap[key] = { ...entry, materials: [...entry.materials] };
    } else {
      const existingDelSet = new Set(splitAndTrim(mergedMap[key].oldDeliveryNo));
      splitAndTrim(entry.oldDeliveryNo).forEach((d) => existingDelSet.add(d));
      mergedMap[key].oldDeliveryNo = Array.from(existingDelSet).join(",");
      mergedMap[key].materials = [...mergedMap[key].materials, ...entry.materials];
    }
  });
  return Object.values(mergedMap);
};

export const renumberMaterialLines = (entries) =>
  (entries || []).map((entry) => ({
    ...entry,
    materials: Array.isArray(entry.materials)
      ? entry.materials.map((mat, idx) => ({
          ...mat,
          materialLineId: ((idx + 1) * 10).toString().padStart(6, "0"),
        }))
      : entry.materials,
  }));
